import { useState, useCallback, useEffect } from "react";
import { toast } from "sonner";
import { NETWORK_CONFIG } from "@/constants/network";

import { isWeb3AuthEnabled, connectWeb3Auth, signInWithWeb3Auth, logoutWeb3Auth } from "@/lib/web3auth";
import { connectWalletConnect as startWalletConnect, disconnectWalletConnect } from "@/lib/walletconnect";

export const isSocialLoginEnabled = isWeb3AuthEnabled;

/** Connect/disconnect via injected wallet (MetaMask) or Web3Auth social login. */
export function useWalletConnection({ providerRef, setChainId, loadBalances, clearBalances }) {
  const [address, setAddress] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);

  const reset = useCallback(() => {
    providerRef.current = null;
    setAddress(null);
    clearBalances();
    setChainId(null);
  }, [providerRef, clearBalances, setChainId]);

  const finishConnect = useCallback(async (provider, accounts) => {
    providerRef.current = provider;
    setAddress(accounts[0]);
    setChainId(parseInt(await provider.request({ method: "eth_chainId" }), 16));
    await loadBalances(accounts[0]);
    toast.success("Wallet connected successfully");
    return accounts[0];
  }, [providerRef, setChainId, loadBalances]);

  const connect = useCallback(async () => {
    if (!window.ethereum) {
      toast.error(isSocialLoginEnabled ? "MetaMask not detected. Install MetaMask or use social login." : "MetaMask not detected. Please install MetaMask.");
      return;
    }
    setIsConnecting(true);
    try {
      const accounts = await window.ethereum.request({ method: "eth_requestAccounts" });
      if (accounts.length > 0) return await finishConnect(window.ethereum, accounts);
    } catch (err) {
      toast.error(err.code === 4001 ? "Connection rejected by user" : "Failed to connect wallet");
    } finally {
      setIsConnecting(false);
    }
  }, [finishConnect]);

  const connectSocial = useCallback(async () => {
    if (!isSocialLoginEnabled) {
      toast.error("Social login is not configured");
      return;
    }
    setIsConnecting(true);
    try {
      const { web3auth, provider } = await connectWeb3Auth();
      const accounts = await provider.request({ method: "eth_accounts" });
      if (!accounts?.length) throw new Error("No wallet address returned");
      const addr = await finishConnect(provider, accounts);
      // Server verifies the Web3Auth ID token (JWKS) and opens the FSC wallet session
      signInWithWeb3Auth(web3auth, addr)
        .then(() => window.dispatchEvent(new Event("fsc:wallet-session")))
        .catch(() => toast.info("Wallet connected. Sign in on the Account page to unlock member perks."));
      return addr;
    } catch (err) {
      const msg = String(err?.message || "");
      if (/not ready|project/i.test(msg)) { toast.error("Social login is unavailable right now. Please use MetaMask."); return; }
      if (!/closed|cancel|rejected/i.test(msg)) toast.error("Social login failed");
    } finally {
      setIsConnecting(false);
    }
  }, [finishConnect]);

  // WalletConnect session (Azaance and other mobile wallets); onUri feeds the QR code
  const connectWalletConnect = useCallback(async (onUri) => {
    setIsConnecting(true);
    try {
      const provider = await startWalletConnect(onUri);
      const accounts = await provider.request({ method: "eth_accounts" });
      if (!accounts?.length) throw new Error("No wallet address returned");
      return await finishConnect(provider, accounts);
    } catch (err) {
      if (!/closed|cancel|rejected|expired/i.test(String(err?.message || ""))) toast.error("Wallet connection failed");
      return null;
    } finally {
      setIsConnecting(false);
    }
  }, [finishConnect]);

  const disconnect = useCallback(() => {
    const provider = providerRef.current;
    if (provider?.isWalletConnect) disconnectWalletConnect(provider);
    else if (provider && provider !== window.ethereum) logoutWeb3Auth();
    reset();
    toast.info("Wallet disconnected");
  }, [providerRef, reset]);

  // Follow account/network switches made inside the wallet while connected
  useEffect(() => {
    const provider = providerRef.current;
    if (!address || !provider?.on) return undefined;
    const onAccounts = (accounts) => {
      if (!accounts?.length) return reset();
      setAddress(accounts[0]);
      loadBalances(accounts[0]);
    };
    const onChain = (id) => setChainId(parseInt(id, 16));
    provider.on("accountsChanged", onAccounts);
    provider.on("chainChanged", onChain);
    return () => {
      provider.removeListener?.("accountsChanged", onAccounts);
      provider.removeListener?.("chainChanged", onChain);
    };
  }, [address, providerRef, reset, loadBalances, setChainId]);

  return { address, isConnecting, connect, connectSocial, connectWalletConnect, disconnect };
}
